import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Building2 } from 'lucide-react';
import type { LocationSelectorProps } from './LocationSelector.types';
import type { Location } from '@/context/AuthContext';

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  locations,
  selectedLocation,
  onLocationSelect,
  isLoading = false,
}) => {
  // Como no hay companyName en la interfaz, agrupar todas las ubicaciones
  const groupedLocations = {
    'Macartur': locations
  };

  const handleLocationClick = (location: Location) => {
    // Si la ubicación ya está seleccionada, deseleccionar
    if (selectedLocation?.id === location.id) {
      onLocationSelect(null);
    } else {
      onLocationSelect(location);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, location: Location) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLocationClick(location);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const currentIndex = locations.findIndex(loc => loc.id === location.id);
      const nextIndex = (currentIndex + 1) % locations.length;
      const nextButton = document.querySelector(`[data-location-id="${locations[nextIndex].id}"]`) as HTMLButtonElement;
      nextButton?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const currentIndex = locations.findIndex(loc => loc.id === location.id);
      const prevIndex = currentIndex === 0 ? locations.length - 1 : currentIndex - 1;
      const prevButton = document.querySelector(`[data-location-id="${locations[prevIndex].id}"]`) as HTMLButtonElement;
      prevButton?.focus();
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-md mx-auto">
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <p className="text-gray-600">Cargando locales...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="w-full max-w-md mx-auto">
        <Card>
          <CardContent className="text-center py-8">
            <MapPin className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">No hay locales disponibles</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-macartur-primary rounded-full flex items-center justify-center mb-4">
            <span className="text-white text-2xl font-bold">M</span>
          </div>
          <CardTitle className="text-macartur-accent1">Selecciona tu local</CardTitle>
          <p className="text-gray-600 text-sm">Elige el local donde trabajarás hoy</p>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {Object.entries(groupedLocations).map(([companyName, companyLocations]) => (
            <div key={companyName}>
              {Object.keys(groupedLocations).length > 1 && (
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <h3 className="font-medium text-macartur-accent1">{companyName}</h3>
                </div>
              )}
              
              <div className="space-y-2">
                {companyLocations.map((location) => (
                  <Button
                    key={location.id}
                    data-location-id={location.id}
                    variant={selectedLocation?.id === location.id ? "default" : "outline"}
                    className={`w-full h-12 justify-start ${
                      selectedLocation?.id === location.id ? 'bg-macartur-primary hover:bg-macartur-primary/90' : ''
                    }`}
                    onClick={() => handleLocationClick(location)}
                    onKeyDown={(e) => handleKeyDown(e, location)}
                    disabled={isLoading}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    <div className="flex-1 text-left">
                      <div className="font-medium">{location.name}</div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};